import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { marketplaceAPI, offersAPI, messagesAPI, shippingAPI, servicesAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OrderSuppliesButton from "@/components/OrderSuppliesButton";
import ReviewsSection from "@/components/ReviewsSection";
import ReportDialog from "@/components/ReportDialog";
import { conditionMeta, money, categoryLabel, MESSAGE_TEMPLATES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  ArrowLeft, Heart, ShoppingCart, MessageCircle, MapPin, Truck, Star, Loader2,
  ImageOff, Tag, Package, ShieldCheck, PackageCheck, Info, Pencil, Trash2, Store,
} from "lucide-react";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [saved, setSaved] = useState(false);
  const [storeSummary, setStoreSummary] = useState(null);

  const [msgOpen, setMsgOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const [offerOpen, setOfferOpen] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [offering, setOffering] = useState(false);

  const [buyerZip, setBuyerZip] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState(null);

  const viewedRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    marketplaceAPI
      .getListing(id)
      .then((res) => {
        setListing(res.data);
        if (res.data?.seller_id) servicesAPI.getStorefrontSummary(res.data.seller_id).then((s) => setStoreSummary(s.data)).catch(() => {});
      })
      .catch(() => toast.error("Listing not found"))
      .finally(() => setLoading(false));
    if (viewedRef.current !== id) {
      viewedRef.current = id;
      marketplaceAPI.incrementListingView(id).catch(() => {});
    }
    offersAPI
      .checkSavedListing(id)
      .then((r) => setSaved(r.data?.is_saved || r.data?.saved || false))
      .catch(() => {});
  }, [id]);

  const reload = () => {
    marketplaceAPI.getListing(id).then((res) => setListing(res.data)).catch(() => {});
  };

  const currency = listing?.currency || "USD";

  const canPickup = useMemo(() => {
    if (!listing) return false;
    return ["local_only", "both"].includes(listing.shipping_option) || listing.local_pickup_available;
  }, [listing]);
  const canShip = useMemo(() => {
    if (!listing) return false;
    return ["ship_only", "both"].includes(listing.shipping_option);
  }, [listing]);

  const shippingStatus = useMemo(() => {
    if (!listing) return null;
    if (listing.has_original_packaging === true) return { ready: true, text: "Seller has original packaging" };
    if (listing.supplies_available_to_buyer === true) return { ready: true, text: "Shipping supplies ready" };
    if (listing.seller_buying_supplies === false)
      return { ready: false, text: "Seller will order supplies after purchase" };
    return { ready: true, text: "Ships direct from seller" };
  }, [listing]);

  const toggleSave = async () => {
    try {
      if (saved) { toast.message("Already in your saved listings"); return; }
      await offersAPI.saveListing(id);
      setSaved(true);
      toast.success("Saved to your listings");
    } catch { toast.error("Could not save listing"); }
  };

  const openMessage = (prefill = "") => {
    setMessage(prefill || `Hi, is the ${listing.title} still available?`);
    setMsgOpen(true);
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const res = await messagesAPI.startConversation({
        recipient_id: listing.seller_id,
        content: message,
        context_type: "marketplace",
        context_id: listing.id,
        context_title: listing.title,
      });
      toast.success("Message sent");
      setMsgOpen(false);
      const convId = res.data?.id || res.data?.conversation_id;
      navigate(convId ? `/messages/${convId}` : "/messages");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  const submitOffer = async () => {
    const amt = parseFloat(offerAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid offer amount"); return; }
    setOffering(true);
    try {
      const shipping = quote?.total_cost ?? quote?.shipping_cost;
      await marketplaceAPI.createOffer({
        listing_id: listing.id,
        offer_amount: amt,
        message: offerMessage || undefined,
        ...(shipping ? { requires_shipping: true, shipping_cost: Number(shipping) } : {}),
      });
      toast.success("Offer sent to the seller");
      setOfferOpen(false);
      setOfferAmount("");
      setOfferMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit offer");
    } finally {
      setOffering(false);
    }
  };

  const getShippingQuote = async () => {
    if (!buyerZip.trim()) { toast.error("Enter your zip / postal code"); return; }
    setQuoting(true);
    try {
      const res = await shippingAPI.calculateQuote({
        product_category: listing.shipping_category || listing.category,
        box_size: listing.shipping_box_size_type || "large",
        origin_zip: listing.seller_zip_code || "",
        destination_zip: buyerZip.trim(),
        declared_value: listing.price,
      });
      setQuote(res.data);
      toast.success("Shipping quote calculated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not fetch shipping quote");
    } finally {
      setQuoting(false);
    }
  };

  const goCheckout = (pickup) => {
    const params = new URLSearchParams();
    if (pickup) params.set("pickup", "true");
    if (!pickup) {
      const shipping = quote?.total_cost ?? quote?.shipping_cost;
      if (shipping) params.set("shipping_cost", String(shipping));
    }
    const qs = params.toString();
    navigate(qs ? `/checkout/${listing.id}?${qs}` : `/checkout/${listing.id}`);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!listing) return null;

  const cond = conditionMeta(listing.condition);
  const isOwner = user?.id === listing.seller_id;
  const sold = listing.status === "sold";
  const images = listing.images?.length ? listing.images : [];

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="listing-back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square overflow-hidden rounded-xl border border-border bg-secondary">
            {images[activeImg] ? (
              <img src={images[activeImg]} alt={listing.title} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageOff className="h-12 w-12" /></div>
            )}
            {sold && <span className="absolute bottom-3 right-3 rounded bg-muted-foreground/90 px-3 py-1 text-xs font-bold uppercase text-background">Sold</span>}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border-2 ${i === activeImg ? "border-primary" : "border-border"}`}>
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <Badge className="border-0 text-background" style={{ backgroundColor: cond.color }}>{cond.label}</Badge>
              <span className="text-sm text-muted-foreground">{categoryLabel(listing.category)}</span>
            </div>
            <h1 className="mt-2 font-display text-3xl font-bold">{listing.title}</h1>
            {listing.brand && <p className="text-muted-foreground">{listing.brand} {listing.model_name}</p>}
            <p className="mt-3 font-display text-4xl font-bold text-primary">{money(listing.price, currency)}</p>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {listing.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {listing.location}</span>}
            <span className="flex items-center gap-1">
              <Truck className="h-4 w-4" />
              {canShip && canPickup ? "Ship or local pickup" : canShip ? "Shipping available" : "Local pickup only"}
            </span>
          </div>

          {listing.description && (
            <div>
              <h3 className="mb-1 font-semibold">Description</h3>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">{listing.description}</p>
            </div>
          )}

          {(listing.whats_included || listing.year_purchased || listing.original_price) && (
            <Card className="border-border bg-card p-4">
              <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider">Details</h3>
              <div className="space-y-1 text-sm">
                {listing.whats_included && <div><span className="text-muted-foreground">Included: </span>{listing.whats_included}</div>}
                {listing.year_purchased && <div><span className="text-muted-foreground">Year purchased: </span>{listing.year_purchased}</div>}
                {listing.original_price && <div><span className="text-muted-foreground">Original price: </span>{money(listing.original_price, currency)}</div>}
              </div>
            </Card>
          )}

          {/* Shipping status */}
          {canShip && (
            <Card className="flex items-start gap-3 border-border bg-card p-4" data-testid="listing-shipping-status">
              <PackageCheck className={`mt-0.5 h-5 w-5 ${shippingStatus?.ready ? "text-[hsl(145,70%,50%)]" : "text-[hsl(45,100%,55%)]"}`} />
              <div className="flex-1">
                <p className="text-sm font-semibold">{shippingStatus?.ready ? "Ready to ship" : "Shipping supplies pending"}</p>
                <p className="text-xs text-muted-foreground">{shippingStatus?.text}</p>
              </div>
            </Card>
          )}

          {/* Shipping quote */}
          {canShip && !isOwner && (
            <Card className="border-border bg-card p-4">
              <h3 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider">
                <Truck className="h-4 w-4 text-primary" /> Calculate shipping to your zip
              </h3>
              <div className="flex gap-2">
                <Input value={buyerZip} onChange={(e) => setBuyerZip(e.target.value)} placeholder="Your zip / postal code" data-testid="listing-quote-zip" />
                <Button onClick={getShippingQuote} disabled={quoting} variant="outline" data-testid="listing-quote-btn">
                  {quoting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get quote"}
                </Button>
              </div>
              {quote && (
                <div className="mt-3 rounded-md bg-secondary/40 p-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Item</span><span>{money(listing.price, currency)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{money(quote.total_cost || quote.shipping_cost || 0, currency)}</span></div>
                  <div className="flex justify-between border-t border-border pt-2 mt-2 font-bold"><span>Total</span><span className="text-primary">{money((listing.price || 0) + (quote.total_cost || quote.shipping_cost || 0), currency)}</span></div>
                </div>
              )}
            </Card>
          )}

          {/* Seller */}
          <Card className="flex items-center gap-3 border-border bg-card p-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={storeSummary?.profile_image || listing.seller_avatar} />
              <AvatarFallback className="bg-secondary">{(listing.seller_username || "S").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-medium">{listing.seller_username}</p>
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <Star className="h-3 w-3 text-accent" /> {listing.seller_rating || 0} · {listing.seller_level} seller
              </p>
            </div>
            {user && !isOwner && (
              <ReportDialog contentType="marketplace" contentId={listing.id} />
            )}
          </Card>

          {storeSummary?.has_storefront && (
            <Link to={`/storefront/${storeSummary.slug || listing.seller_id}`} className="flex items-center gap-2 rounded-lg border-2 border-primary/60 bg-primary/5 p-3 transition-colors hover:border-primary hover:bg-primary/10" data-testid="listing-storefront-link">
              {storeSummary.profile_image ? (
                <img src={storeSummary.profile_image} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <Store className="h-5 w-5 text-primary" />
              )}
              <div className="flex-1">
                <p className="text-sm font-semibold">{storeSummary.brand_name || listing.seller_username}'s storefront</p>
                <p className="text-xs font-medium text-primary">View storefront →</p>
              </div>
            </Link>
          )}

          {/* Actions */}
          {!isOwner && !sold && (
            <div className="space-y-2">
              {canShip && (
                <Button
                  onClick={() => (shippingStatus?.ready ? goCheckout(false) : openMessage("Hi! I'm interested in buying this item. Can you let me know when you'll be ready to ship?"))}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="listing-buy-shipping"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  {shippingStatus?.ready ? "Buy with shipping" : "Message seller (supplies pending)"}
                </Button>
              )}
              {canPickup && (
                <Button onClick={() => goCheckout(true)} variant="outline" className="w-full" data-testid="listing-buy-pickup">
                  <MapPin className="mr-2 h-4 w-4" /> Buy with local pickup
                </Button>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Button onClick={() => { setOfferAmount(String(listing.price ?? "")); setOfferOpen(true); }} variant="outline" data-testid="listing-offer-button">
                  <Tag className="mr-1 h-4 w-4" /> Offer
                </Button>
                <Button onClick={() => openMessage("")} variant="outline" data-testid="listing-message-button">
                  <MessageCircle className="mr-1 h-4 w-4" /> Message
                </Button>
                <Button onClick={toggleSave} variant="outline" data-testid="listing-save-button">
                  <Heart className={`h-4 w-4 ${saved ? "fill-primary text-primary" : ""}`} />
                </Button>
              </div>
            </div>
          )}
          {isOwner && (
            <div className="space-y-2">
              <p className="rounded-md border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">This is your listing.</p>
              {!listing.has_original_packaging && listing.shipping_category && (
                <div className="rounded-md border border-border bg-secondary/30 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Shipping supplies</p>
                  <OrderSuppliesButton listing={listing} onDone={reload} testId="listing-supplies" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="border-border"
                  onClick={() => navigate(`/marketplace/${listing.id}/edit`)}
                  data-testid="listing-edit-btn"
                >
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setConfirmDelete(true)}
                  data-testid="listing-delete-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </Button>
              </div>
            </div>
          )}
          {sold && !isOwner && (
            <p className="rounded-md border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">This item has been sold.</p>
          )}

          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" /> Payment held securely until you confirm delivery.
          </p>
        </div>
      </div>

      <div className="mt-8">
        <ReviewsSection sellerId={listing.seller_id} kind="product" />
      </div>

      {/* Message dialog */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Message {listing.seller_username}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2">
            {MESSAGE_TEMPLATES.map((t) => (
              <button key={t} type="button" onClick={() => setMessage(t)} className="rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs hover:border-primary" data-testid={`listing-template-${t.slice(0, 10)}`}>
                {t}
              </button>
            ))}
          </div>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="listing-message-textarea" />
          <DialogFooter>
            <Button onClick={sendMessage} disabled={sending} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="listing-message-send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer dialog */}
      <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Make an offer</DialogTitle></DialogHeader>
          <div className="rounded-md bg-secondary/40 p-3 text-sm">
            <p><span className="text-muted-foreground">Listed at </span><span className="font-semibold">{money(listing.price, currency)}</span></p>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-wider">Your offer *</label>
            <Input type="number" step="0.01" min="0" value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} placeholder={`${Math.round(listing.price || 0)}`} data-testid="listing-offer-amount" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-wider">Message (optional)</label>
            <Textarea value={offerMessage} onChange={(e) => setOfferMessage(e.target.value)} placeholder="Add a note to your offer..." rows={3} data-testid="listing-offer-message" />
          </div>

          {/* Shipping added to the offer */}
          {canShip && (
            (quote?.total_cost ?? quote?.shipping_cost) ? (
              <div className="space-y-1 rounded-md border border-primary/30 bg-primary/10 p-3 text-sm" data-testid="listing-offer-shipping">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Shipping (quote)</span>
                  <span className="font-semibold">{money(quote.total_cost ?? quote.shipping_cost, currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold">Total offer</span>
                  <span className="font-bold text-primary">{money((parseFloat(offerAmount) || 0) + Number(quote.total_cost ?? quote.shipping_cost), currency)}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3" data-testid="listing-offer-quote-prompt">
                <p className="text-xs text-muted-foreground">Get a shipping quote to add shipping to your offer.</p>
                <div className="flex gap-2">
                  <Input value={buyerZip} onChange={(e) => setBuyerZip(e.target.value)} placeholder="Your zip / postal code" data-testid="listing-offer-quote-zip" />
                  <Button onClick={getShippingQuote} disabled={quoting} variant="outline" data-testid="listing-offer-quote-btn">
                    {quoting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get quote"}
                  </Button>
                </div>
              </div>
            )
          )}

          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 flex-shrink-0" /> Sellers can accept, decline or counter. Offers are visible in Messages.
          </p>
          <DialogFooter>
            <Button onClick={submitOffer} disabled={offering} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="listing-offer-send">
              {offering ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send offer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete this listing?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently remove <span className="text-foreground">&ldquo;{listing.title}&rdquo;</span> from the marketplace. Buyers won&apos;t be able to see it or send new offers. This can&apos;t be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                setDeleting(true);
                try {
                  await marketplaceAPI.deleteListing(listing.id);
                  toast.success("Listing deleted");
                  navigate("/marketplace");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Could not delete listing");
                } finally {
                  setDeleting(false);
                  setConfirmDelete(false);
                }
              }}
              disabled={deleting}
              data-testid="listing-delete-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete listing"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
